(() => {
  if (customElements.get('lgd-product-discovery')) return;

  const historyKey = 'shopify_recently_viewed';
  const validHandle = value => /^[a-z0-9][a-z0-9_-]*$/i.test(value);
  const unique = values => [...new Set(values.filter(validHandle))];
  const cardRequests = new Map();

  function readHistory() {
    let local = '';
    let cookie = '';
    try { local = localStorage.getItem(historyKey) || ''; } catch (_) { /* Storage can be unavailable. */ }
    try {
      const entry = document.cookie.split('; ').find(value => value.startsWith(`${historyKey}=`));
      cookie = entry ? decodeURIComponent(entry.slice(historyKey.length + 1)) : '';
    } catch (_) { /* Invalid or unavailable cookies must not interrupt browsing. */ }
    return unique(`${cookie} ${local}`.split(/\s+/));
  }

  function recordHistory(current, history, days) {
    if (!validHandle(current)) return;
    const value = unique([current, ...history]).slice(0, 10).join(' ');
    try { localStorage.setItem(historyKey, value); } catch (_) { /* Keep cookie fallback. */ }
    try {
      const maxAge = Math.max(1, Math.min(365, Number(days) || 90)) * 86400;
      document.cookie = `${historyKey}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
    } catch (_) { /* A disabled cookie does not prevent rendering existing history. */ }
  }

  async function fetchDocument(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error(`Product section unavailable: ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } finally {
      clearTimeout(timer);
    }
  }

  class ProductDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.grid = this.querySelector('[data-discovery-grid]');
      if (!this.grid) return;
      this.limit = Math.max(1, Math.min(6, Number(this.dataset.limit) || 6));
      if (this.dataset.kind === 'recent') {
        const history = readHistory();
        this.handles = history.filter(handle => handle !== this.dataset.currentHandle).slice(0, this.limit);
        recordHistory(this.dataset.currentHandle, history, this.dataset.expireDays);
        if (!this.handles.length) return;
      }
      const load = () => this.load().catch(() => { this.hidden = true; });
      if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 1200 });
      else setTimeout(load, 0);
    }

    async load() {
      if (this.dataset.kind === 'related') {
        if (!this.grid.querySelector('.lgd-card')) {
          const document = await fetchDocument(this.dataset.url);
          const section = document.getElementById(this.id);
          const grid = section && section.querySelector('[data-discovery-grid]');
          if (grid) this.grid.replaceChildren(...grid.children);
        }
      } else {
        // Fetch in pairs, preserving the most-recent-first order even when requests finish out of order.
        const cards = [];
        for (let start = 0; start < this.handles.length; start += 2) {
          const results = await Promise.allSettled(this.handles.slice(start, start + 2).map(handle => this.recentCard(handle)));
          results.forEach(result => {
            if (result.status === 'fulfilled' && result.value) cards.push(result.value.cloneNode(true));
          });
        }
        this.grid.replaceChildren(...cards);
      }
      if (!this.isConnected) return;
      this.hidden = !this.grid.querySelector('.lgd-card');
    }

    recentCard(handle) {
      const url = `${this.dataset.productsRoot}${encodeURIComponent(handle)}?view=lgd-discovery-card`;
      if (!cardRequests.has(url)) {
        cardRequests.set(url, fetchDocument(url).then(document => document.querySelector('.lgd-card')));
      }
      return cardRequests.get(url);
    }
  }

  customElements.define('lgd-product-discovery', ProductDiscovery);
})();
